
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastService } from 'src/app/core/services/toast.service';
import { EStampService } from 'src/app/core/services/e-stamp.service';
import { Router } from '@angular/router';
import * as XLSX from 'xlsx';
import { ApiValidationError } from 'src/app/shared/models/api-validation.model';
import { normalizeApiValidationError } from 'src/app/shared/utils/api-validation.util';


@Component({
  selector: 'app-upload-stamp-excel',
  templateUrl: './upload-stamp-excel.component.html',
  styleUrl: './upload-stamp-excel.component.scss',
  standalone: false
})
export class UploadStampExcel implements OnInit {
  activeTab: 'onDemand' | 'bulkCreate' | 'bulkUpload' = 'bulkUpload';
  @ViewChild('fileInput') fileInput: any;

  uploadForm!: FormGroup;
  uploadedFile: File | null = null;
  uploading: boolean = false;
  downloadingTemplate: boolean = false;
  uploadErrors: any[] = [];
  uploadSuccess: boolean = false;
  excelData: any[] = [];
  totalAmountFromExcel: number = 0;
  apiValidationError: ApiValidationError | null = null;
  fileSizeError: string | null = null;
  readonly maxFileSizeBytes: number = 1 * 1024 * 1024; // 1MB

  constructor
    (
      private fb: FormBuilder,
      private eStampService: EStampService,
      private router: Router,
      private toastService: ToastService,
    ) { }

  ngOnInit() {
    this.initializeUploadForm();
  }

  switchTab(tab: 'onDemand' | 'bulkCreate' | 'bulkUpload') {
    this.activeTab = tab;
    this.uploadErrors = [];
  }

  initializeUploadForm() {
    this.uploadForm = this.fb.group({
      file: ['', Validators.required]
    });
  }

  downloadTemplate() {
    if (this.downloadingTemplate) return;

    this.downloadingTemplate = true;

    const link = document.createElement('a');
    link.href = 'assets/files/Bulk_Order_Template.xlsx';
    link.download = 'Bulk_Order_Template.xlsx';
    link.click();
    this.downloadingTemplate = false;
    this.toastService.showToaster('Success', 'Template downloaded successfully', 'success');
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    this.fileSizeError = null;

    if (file) {
      const validTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv'];

      if (!validTypes.includes(file.type)) {
        this.toastService.showToaster('Warning', 'Please select a valid Excel or CSV file', 'info');
        return;
      }

      if (file.size > this.maxFileSizeBytes) {
        this.fileSizeError = `File size (${(file.size / (1024 * 1024)).toFixed(2)} MB) exceeds the maximum limit of 1MB. Please upload a smaller file.`;
        this.toastService.showToaster('Warning', this.fileSizeError, 'info');
        if (this.fileInput) {
          this.fileInput.nativeElement.value = '';
        }
        return;
      }

      this.validateExcelFile(file);
    }
  }

  validateExcelFile(file: File) {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          this.toastService.showToaster('Warning', 'Excel file is empty', 'info');
          return;
        }

        const validationErrors = this.validateExcelStructure(jsonData);

        if (validationErrors.length > 0) {
          this.uploadErrors = validationErrors;
          this.totalAmountFromExcel = 0;
          this.toastService.showToaster(
            'Validation Error',
            `Found ${validationErrors.length} validation error(s). Please check below.`,
            'error'
          );
          return;
        }

        this.excelData = jsonData;
        this.calculateExcelTotal();
        this.uploadedFile = file;
        this.uploadForm.patchValue({ file: file.name });
        this.uploadErrors = [];
        this.toastService.showToaster('Success', 'File validation passed', 'success');
      } catch (error: any) {
        this.toastService.showToaster('Error', 'Failed to read Excel file. Please ensure it is a valid Excel or CSV file.', 'error');
      }
    };
    reader.readAsArrayBuffer(file);
  }

  calculateExcelTotal() {
    this.totalAmountFromExcel = 0;
    const columnHeaders = this.excelData.length > 0 ? Object.keys(this.excelData[0]).map(col => col.trim()) : [];
    const denominationKey = columnHeaders.find(h => h.toLowerCase() === 'denomination');
    const quantityKey = columnHeaders.find(h => h.toLowerCase() === 'quantity');

    if (!denominationKey || !quantityKey) {
      this.totalAmountFromExcel = 0;
      return;
    }

    this.excelData.forEach(row => {
      const denomination = parseFloat(row[denominationKey]) || 0;
      const quantity = parseInt(row[quantityKey]) || 0;
      this.totalAmountFromExcel += denomination * quantity;
    });
  }

  validateExcelStructure(data: any[]): any[] {
    const errors: any[] = [];
    const requiredColumns = ['State', 'Denomination', 'Quantity'];

    if (data.length === 0) {
      errors.push({ row: 1, message: 'Excel file is empty' });
      return errors;
    }

    const firstRow = data[0];
    const columnHeaders = Object.keys(firstRow).map(col => col.trim());

    const missingColumns = requiredColumns.filter(
      col => !columnHeaders.some(header => header.toLowerCase() === col.toLowerCase())
    );

    if (missingColumns.length > 0) {
      errors.push({
        row: 1,
        message: `Missing required columns: ${missingColumns.join(', ')}`
      });
    }

    const denominationKey = columnHeaders.find(h => h.toLowerCase() === 'denomination');
    const quantityKey = columnHeaders.find(h => h.toLowerCase() === 'quantity');
    const stateKey = columnHeaders.find(h => h.toLowerCase() === 'state');

    // Track State + Denomination combinations to detect duplicate rows
    const combinationRows = new Map<string, number[]>();

    data.forEach((row, index) => {
      const rowNumber = index + 2;

      // Check State is not empty
      if (stateKey) {
        const state = row[stateKey];
        if (!state || (typeof state === 'string' && state.trim() === '')) {
          errors.push({
            row: rowNumber,
            message: 'State is required but missing'
          });
        }
      }

      // Strict Denomination validation - must be a number only

      if (denominationKey) {
        const denomination = row[denominationKey];

        if (denomination === null || denomination === undefined || denomination === '') {
          errors.push({
            row: rowNumber,
            message: 'Denomination is required. Must be a number only'
          });
        } else {
          const denominationStr = denomination.toString().trim();
          if (!/^\d+(\.\d+)?$/.test(denominationStr) || isNaN(parseFloat(denominationStr)) || parseFloat(denominationStr) <= 0) {
            errors.push({
              row: rowNumber,
              message: `Denomination must be a positive number only. Got: "${denomination}"`
            });
          }
        }
      }

      // Strict Quantity validation - must be a positive integer only
      if (quantityKey) {
        const quantity = row[quantityKey];

        if (quantity === null || quantity === undefined || quantity === '') {
          errors.push({
            row: rowNumber,
            message: 'Quantity is required. Must be a positive whole number only'
          });
        } else {
          const quantityStr = quantity.toString().trim();
          if (!/^\d+$/.test(quantityStr) || parseInt(quantityStr) < 1) {
            errors.push({
              row: rowNumber,
              message: `Quantity must be a positive whole number only (no decimals). Got: "${quantity}"`
            });
          }
        }
      }

      // Track State + Denomination combination for duplicate detection
      if (stateKey && denominationKey) {
        const state = row[stateKey];
        const denomination = row[denominationKey];

        if (state && denomination !== null && denomination !== undefined && denomination !== '') {
          const stateNormalized = state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();
          const denominationNormalized = parseFloat(denomination.toString().trim());

          if (stateNormalized && !isNaN(denominationNormalized)) {
            const combinationKey = `${stateNormalized}|${denominationNormalized}`;
            if (!combinationRows.has(combinationKey)) {
              combinationRows.set(combinationKey, []);
            }
            combinationRows.get(combinationKey)!.push(rowNumber);
          }
        }
      }
    });

    // Flag duplicate State + Denomination combinations across rows (one error per group)
    combinationRows.forEach((rows, combinationKey) => {
      if (rows.length > 1) {
        const [stateNormalized, denominationNormalized] = combinationKey.split('|');
        errors.push({
          row: rows[0],
          message: `Duplicate entry: State "${stateNormalized}" with Denomination "${denominationNormalized}" appears in row(s) ${rows.join(', ')}. Please combine into a single row by adjusting the Quantity instead.`
        });
      }
    });

    return errors;
  }

  onSubmitExcelUpload() {
    if (this.uploading || !this.uploadedFile) {
      this.toastService.showToaster('Warning', 'Please select a file', 'info');
      return;
    }

    this.uploading = true;
    this.uploadErrors = [];
    this.apiValidationError = null;

    this.eStampService.uploadBulkOrdersExcel(this.uploadedFile).subscribe({
      next: (res: any) => {
        this.uploading = false;

        if (res?.errors && res.errors.length > 0) {
          this.uploadErrors = res.errors;
          this.toastService.showToaster(
            'Partial Success',
            `${res?.successCount || 0} orders created with ${res.errors.length} errors`,
            'info'
          );
        } else {
          this.uploadedFile = null;
          this.uploadForm.reset();
          this.toastService.showToaster('Success', res?.message || 'All orders uploaded successfully', 'success');
          setTimeout(() => {
            this.router.navigate(['/customer/estamp/bulk-order']);
          });
        }
      },
      error: (err: any) => {
        this.uploading = false;
        this.apiValidationError = normalizeApiValidationError(err);
      }
    });
  }

  clearUploadForm() {
    this.uploadedFile = null;
    this.uploadForm.reset();
    this.uploadErrors = [];
    this.excelData = [];
    this.totalAmountFromExcel = 0;
    this.fileSizeError = null;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  clearApiValidationMessage(): void {
    this.apiValidationError = null;
  }

}
